import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { requestEmailLogin, verifyEmailOTP } from "../../services/auth";
import { validateEmail } from "../../utils/validation";
import { useTurnstile } from "../../hooks/useTurnstile";

import AuthNavbar from "./AuthNavbar";
import "./Auth.css";

enum LoginStep {
  EMAIL_ENTRY,
  OTP_SENT,
}

interface EmailLoginProps {
  onLoginSuccess: () => void;
}

const EmailLogin: React.FC<EmailLoginProps> = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  
  // Form states
  const [email, setEmail] = useState<string>("");
  const [step, setStep] = useState<LoginStep>(LoginStep.EMAIL_ENTRY);
  const [otp, setOtp] = useState<string>("");

  // UI states
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [otpLoading, setOtpLoading] = useState<boolean>(false);
  const [emailValidationMsg, setEmailValidationMsg] = useState<string | null>(null);

  // OTP input refs for auto-focus
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const turnstileerror = useCallback(() => {
    setError("Security verification failed. Please try again.");
  }, []);
  const turnstileexpire = useCallback(() =>  {
    setError("Security verification expired. Please try again.");
  }, []);
  const { turnstileContainer, getToken, hasToken, resetTurnstile, isDevelopment } = useTurnstile({
    onError: turnstileerror,
    onExpire: turnstileexpire,
  });

  // Initialize OTP input refs
  useEffect(() => {
    otpInputRefs.current = otpInputRefs.current.slice(0, 6);
  }, []);

  // Auto-focus first OTP input when step changes to OTP_SENT
  useEffect(() => {
    if (step === LoginStep.OTP_SENT && otpInputRefs.current[0]) {
      setTimeout(() => {
        otpInputRefs.current[0]?.focus();
      }, 500); // Small delay to ensure the inputs are rendered
    }
  }, [step]);

  // Handle OTP input change
  const handleOtpChange = (index: number, value: string) => {
    // Only allow numeric input
    if (!/^\d*$/.test(value)) return;
    
    const newOtp = otp.split("");
    newOtp[index] = value;
    
    // Update OTP state
    const updatedOtp = newOtp.join("");
    setOtp(updatedOtp);
    
    // Auto-focus next input
    if (value && index < 5 && otpInputRefs.current[index + 1]) {
      otpInputRefs.current[index + 1]?.focus();
    }
    
    // Auto-submit when all 6 digits are entered
    if (updatedOtp.length === 6) {
      handleOtpSubmit(updatedOtp);
    }
  };

  // Handle backspace in OTP input
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      // Focus previous input on backspace if current is empty
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste in OTP input
  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    
    if (pastedData.length > 0) {
      setOtp(pastedData.padEnd(6, ""));
      
      // Focus the next empty input or the last input
      const nextIndex = Math.min(pastedData.length, 5);
      otpInputRefs.current[nextIndex]?.focus();
      
      // Auto-submit if 6 digits pasted
      if (pastedData.length === 6) {
        handleOtpSubmit(pastedData);
      }
    }
  };

  // Handle OTP verification
  const handleOtpSubmit = async (otpCode?: string) => {
    const codeToVerify = otpCode || otp;
    
    if (codeToVerify.length !== 6) {
      setError("Please enter the complete 6-digit code");
      return;
    }

    setOtpLoading(true);
    setError(null);

    try {
      const result = await verifyEmailOTP(email, codeToVerify);

      if (result.success) {
        setSuccess("Login successful! Redirecting...");
        
        // Call the login success handler after a short delay
        setTimeout(() => {
          onLoginSuccess();
        }, 1500);
      } else {
        setError(result.error || "Invalid or expired code. Please try again.");
        // Clear OTP on error
        setOtp("");
        if (otpInputRefs.current[0]) {
          otpInputRefs.current[0].focus();
        }
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
      console.error("OTP verification error:", err);
      // Clear OTP on error
      setOtp("");
      if (otpInputRefs.current[0]) {
        otpInputRefs.current[0].focus();
      }
    } finally {
      setOtpLoading(false);
    }
  };

  // Validate email on blur
  const validateEmailOnBlur = () => {
    setEmailValidationMsg(null);

    if (!email.trim()) {
      setEmailValidationMsg("Email is required");
      return;
    }

    if (!validateEmail(email)) {
      setEmailValidationMsg("Please enter a valid email address");
    }
  };

  // Handle email form submission
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim() || !validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    // Check Turnstile verification
    if (!isDevelopment && !hasToken()) {
      setError("Please complete the security check");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const turnstileToken = getToken();
      const result = await requestEmailLogin(email.trim(), turnstileToken || undefined);

      if (result.success) {
        setSuccess(result.message || "OTP sent to your email");
        setStep(LoginStep.OTP_SENT);
      } else {
        setError(result.error || "Failed to send OTP. Please try again.");
        // Reset the Turnstile widget on error
        resetTurnstile();
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
      console.error(err);
      // Reset the Turnstile widget on error
      resetTurnstile();
    } finally {
      setLoading(false);
    }
  };

  // Handle resend OTP
  const handleResendOTP = async () => {
    // Check Turnstile verification for resend
    if (!isDevelopment && !hasToken()) {
      setError("Please complete the security check");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const turnstileToken = getToken();
      const result = await requestEmailLogin(email.trim(), turnstileToken || undefined);

      if (result.success) {
        setSuccess("New code sent to your email");
        // Clear current OTP
        setOtp("");
        if (otpInputRefs.current[0]) {
          otpInputRefs.current[0].focus();
        }
      } else {
        setError(result.error || "Failed to resend OTP. Please try again.");
        // Reset the Turnstile widget on error
        resetTurnstile();
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
      console.error(err);
      // Reset the Turnstile widget on error
      resetTurnstile();
    } finally {
      setLoading(false);
    }
  };

  // Render email entry step
  const renderEmailEntryStep = () => {
    return (
      <motion.form
        onSubmit={handleEmailSubmit}
        className="space-y-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <motion.div
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <label
            htmlFor="email"
            className="block text-sm font-medium text-[color:var(--foreground)]"
          >
            Email Address
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={validateEmailOnBlur}
            placeholder="Enter your email address"
            autoComplete="email"
            disabled={loading}
            className="mt-1 block w-full px-3 py-2 bg-transparent border border-[color:var(--border)] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-secondary)] focus:border-transparent text-[color:var(--foreground)] [&:-webkit-autofill]:text-[color:var(--foreground)] [&:-webkit-autofill]:bg-transparent [&:-webkit-autofill]:!transition-colors"
          />
          {emailValidationMsg && (
            <motion.p
              className="mt-1 text-sm text-red-600"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              {emailValidationMsg}
            </motion.p>
          )}
        </motion.div>

        {/* Turnstile Widget - Only show in production */}
        {!isDevelopment ? (
          <motion.div
            className="flex justify-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <div ref={turnstileContainer}></div>
          </motion.div>
        ) : (
          <motion.div
            className="p-3 bg-blue-50 border border-blue-200 text-blue-600 rounded-md text-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            Development mode: Turnstile verification bypassed
          </motion.div>
        )}

        {error && (
          <motion.div
            className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <p>{error}</p>
            {/* Show signup link if error suggests account doesn't exist */}
            {(error.includes("not found") || error.includes("not registered") || error.includes("not verified")) && (
              <p className="mt-2 font-medium">
                <Link
                  to="/signup"
                  className="text-[color:var(--color-secondary)] hover:underline"
                >
                  Don't have an account? Sign up here
                </Link>
              </p>
            )}
          </motion.div>
        )}

        {success && (
          <motion.div
            className="p-3 bg-green-50 border border-green-200 rounded-md text-sm"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0, scale: [1, 1.03, 1] }}
            transition={{
              duration: 0.4,
              scale: { duration: 0.5, times: [0, 0.5, 1] },
            }}
          >
            <div className="flex items-center">
              <div className="shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-2">
                <motion.svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-green-600"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                >
                  <path d="M20 6L9 17l-5-5"></path>
                </motion.svg>
              </div>
              <span className="text-green-600 font-medium">{success}</span>
            </div>
          </motion.div>
        )}

        <motion.button
          type="submit"
          disabled={loading}
          className="w-full py-2 px-4 bg-[color:var(--color-secondary)] text-white rounded-md text-sm font-medium hover:bg-[color:var(--color-secondary)]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--color-secondary)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.9 }}
        >
          {loading ? "Sending..." : "Send OTP"}
        </motion.button>
      </motion.form>
    );
  };

  // Render OTP sent step with inline input fields
  const renderOTPSentStep = () => {
    return (
      <motion.div
        className="space-y-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <div className="flex flex-col items-center mb-6">
          <motion.div
            className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <motion.svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-green-600"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
              <polyline points="22,6 12,13 2,6"></polyline>
            </motion.svg>
          </motion.div>

          <motion.h3
            className="text-xl font-bold text-[color:var(--foreground)] text-center"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            Check Your Email
          </motion.h3>

          <motion.p
            className="text-sm text-[color:var(--muted-foreground)] mt-2 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            We've sent a verification code to <strong>{email}</strong>
          </motion.p>
        </div>


        {/* OTP Input Section */}
        <motion.div
          className="space-y-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <div>
            <label className="block text-sm font-medium text-[color:var(--foreground)] mb-3">
              Enter Verification Code
            </label>
            <div className="flex justify-center space-x-2">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <input
                  key={index}
                  ref={(el) => (otpInputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={otp[index] || ""}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  onPaste={handleOtpPaste}
                  disabled={otpLoading}
                  className="w-12 h-12 text-center text-lg font-semibold bg-transparent border-2 border-[color:var(--border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[color:var(--color-secondary)] focus:border-transparent text-[color:var(--foreground)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                />
              ))}
            </div>
          </div>

          {error && (
            <motion.div
              className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <p>{error}</p>
            </motion.div>
          )}



          <motion.button
            onClick={() => handleOtpSubmit()}
            disabled={otpLoading || otp.length !== 6}
            className="w-full py-2 px-4 bg-[color:var(--color-secondary)] text-white rounded-md text-sm font-medium hover:bg-[color:var(--color-secondary)]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--color-secondary)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            {otpLoading ? "Verifying..." : "Verify Code"}
          </motion.button>

          <motion.button
            onClick={handleResendOTP}
            disabled={loading}
            className="w-full py-2 px-4 border border-[color:var(--border)] text-[color:var(--foreground)] rounded-md text-sm font-medium hover:bg-[color:var(--muted)]/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--color-secondary)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.7 }}
          >
            {loading ? "Sending..." : "Resend OTP"}
          </motion.button>

          <motion.div
            className="text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.8 }}
          >
            <button
              onClick={() => setStep(LoginStep.EMAIL_ENTRY)}
              className="text-sm text-[color:var(--muted-foreground)] hover:text-[color:var(--color-secondary)] font-medium"
            >
              ← Back to email entry
            </button>
          </motion.div>
        </motion.div>
      </motion.div>
    );
  };

  // Render appropriate step
  const renderStep = () => {
    switch (step) {
      case LoginStep.EMAIL_ENTRY:
        return renderEmailEntryStep();
      case LoginStep.OTP_SENT:
        return renderOTPSentStep();
      default:
        return renderEmailEntryStep();
    }
  };

  return (
    <>
      <AuthNavbar />
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--background)] py-12 px-4 sm:px-6 lg:px-8 relative pt-20">
        <div className="max-w-md w-full bg-[color:var(--card)] rounded-lg shadow-xl p-8">
          {/* Back button for OTP sent step */}
          {step === LoginStep.OTP_SENT && (
            <div className="flex justify-start mb-4">
              <button
                onClick={() => setStep(LoginStep.EMAIL_ENTRY)}
                className="flex items-center text-sm text-[color:var(--foreground)] hover:text-[color:var(--color-secondary)] transition-colors px-3 py-1.5 rounded-full border border-[color:var(--border)] bg-[color:var(--card)] hover:bg-[color:var(--muted)]/10"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mr-1"
                >
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
                Back to Email
              </button>
            </div>
          )}

          <div className="flex flex-col items-center mb-8">
            <motion.h2
              className="text-3xl pt-2 font-bold text-[color:var(--foreground)]"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Voice Agent Portal
            </motion.h2>
            <motion.p
              className="text-sm text-[color:var(--muted-foreground)] mt-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              {step === LoginStep.EMAIL_ENTRY
                ? "Enter your email address to receive a login code"
                : "Check your email for the verification code"}
            </motion.p>
          </div>

          {renderStep()}

          <motion.div
            className="mt-6 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <Link
              to="/admin/login"
              className="text-sm text-[color:var(--foreground)] hover:text-[color:var(--color-secondary)] font-medium"
            >
              Admin? Login here
            </Link>
            <div className="mt-2">
              <p className="text-sm text-[color:var(--foreground)]">
                <Link
                  to="/signup"
                  className="text-[color:var(--foreground)] hover:text-[color:var(--color-secondary)] font-medium"
                >
                  Don't have an account? Sign up here
                </Link>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default EmailLogin;